import { Button, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ChartColumn } from 'lucide-react';
import { parseAsString, useQueryStates } from 'nuqs';
import { DUTY_ANALYTICS_DRILL_LIMIT, type DashboardTileRowDto, type DutyAnalyticsRowsPageDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { useServerTable } from '@/hooks/useServerTable';
import { ApiError, api } from '@/lib/api';
import {
  DUTY_ANALYTICS_ROWS_PATH,
  divisionPageHref,
  dutyAnalyticsHref,
  parseDrillParams,
  visibleDrillColumns,
  wayBackDivisionOf,
  windowOfTarget,
  windowWords,
  type DrillTarget,
} from '@/lib/dutyAnalytics';
import { istNow } from '@/lib/istNow';
import { queryKeys } from '@/lib/queryKeys';
import { vocabularyWord } from '@/lib/statusPresentation';
import NotFoundPage from '@/pages/NotFound';
import { DUTY_ANALYTICS_COPY } from '@/pages/DutyAnalytics/copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHAT A DUTY ANALYTICS FIGURE IS MADE OF. [P7C §2j]
 *
 *  ⚠️ THE COLUMNS AND THEIR LABELS ARE THE SERVER'S — the dashboard tile drill's shape
 *  (`DashboardTileRowDto`, per-cell `hrefs`), so this page keeps no second list of what a row holds.
 *  The server reads the SAME cell the page did, and `figure` — every page of rows summed — equals the
 *  count the operator clicked, by construction.
 *
 *  ⚠️ A URL THE PAGE COULD NOT HAVE WRITTEN IS NOT FOUND, AND ASKS NOTHING (`DashboardTile`'s rule).
 *
 *  ⚠️ NO COLUMN THAT NAMES A PERSON RENDERS, WHATEVER ARRIVES (§2h). Attendance and markings drill to
 *  DUTIES with counts; a per-officer attendance record is a question the brief asks to report, not to
 *  build. The server's projection names nobody — this is the second half, so a later projection change
 *  cannot put a name on screen through this page.
 *
 *  ⚠️ PAGED BY `useServerTable` OVER A FIXED SERVER ORDER — newest duty first. The hook needs a sort
 *  allowlist, so it is handed one value that is never sent: no column offers a sorter.
 *
 *  ⚠️ [amended P7C.1] A DRILL MAY NAME A CUSTOM WINDOW (`dateFrom`/`dateTo`, sent in place of the range) and the
 *  division it was opened AS (`asDivisionId`, sent with it). A pair is parsed against the IST day from `istNow()`
 *  and a pair the shared rule refuses is not found, like any other URL the page could not have written; so is
 *  `asDivisionId` beside a `divisionId`. The way back from a drill opened AS a division leads to that division's
 *  page, keeping the window and the commissionerate page's narrowing.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;

const NUQS_OPTIONS = { clearOnDefault: true, history: 'replace' } as const;

const DRILL_PARSERS = {
  metric: parseAsString.withDefault(''),
  divisionId: parseAsString.withDefault(''),
  orgUnitId: parseAsString.withDefault(''),
  asDivisionId: parseAsString.withDefault(''),
  range: parseAsString.withDefault(''),
  dateFrom: parseAsString.withDefault(''),
  dateTo: parseAsString.withDefault(''),
  /*
   * ⚠️ THE PAGE'S OWN NARROWING, FOR THE WAY BACK ONLY. Never a filter key and never sent: the cell is
   * `divisionId`/`orgUnitId`, and narrowing the way back by the CELL's division narrowed the Units table
   * of an operator who had not narrowed it (`drillHref`).
   */
  division: parseAsString.withDefault(''),
};
const DRILL_FILTER_KEYS = ['metric', 'divisionId', 'orgUnitId', 'asDivisionId', 'range', 'dateFrom', 'dateTo'] as const;
const DRILL_SORT_FIELDS = ['serverOrder'] as const;

/** ⚠️ The range OR the pair, never both — the server refuses the pair beside a range. */
const drillQuery = (target: DrillTarget, page: number, limit: number) => ({
  metric: target.metric,
  ...(target.cell.divisionId === undefined ? {} : { divisionId: target.cell.divisionId }),
  ...(target.cell.orgUnitId === undefined ? {} : { orgUnitId: target.cell.orgUnitId }),
  ...(target.asDivisionId === undefined ? {} : { asDivisionId: target.asDivisionId }),
  ...('range' in target ? { range: target.range } : { dateFrom: target.dateFrom, dateTo: target.dateTo }),
  page,
  limit,
});

/** Back to the page the drill came from: a division's own page when it was read AS one, else the page. */
const wayBackOf = (target: DrillTarget, division: string): string =>
  target.asDivisionId === undefined
    ? dutyAnalyticsHref(windowOfTarget(target), wayBackDivisionOf(division))
    : divisionPageHref(target.asDivisionId, windowOfTarget(target), wayBackDivisionOf(division));

const DutyAnalyticsRowsPage = () => {
  const [raw] = useQueryStates(DRILL_PARSERS, NUQS_OPTIONS);
  /* [P7C.1 §5d] The IST day, from the one door — a custom pair is checked against it, never the browser's day. */
  const today = istNow().toISODate() ?? undefined;
  const target = parseDrillParams(raw, today);

  const table = useServerTable<DashboardTileRowDto, 'serverOrder', DutyAnalyticsRowsPageDto>({
    sortFields: DRILL_SORT_FIELDS,
    defaultSortBy: 'serverOrder',
    defaultLimit: DUTY_ANALYTICS_DRILL_LIMIT,
    filterKeys: DRILL_FILTER_KEYS,
    queryKey: (params) =>
      queryKeys.dutyAnalytics.rows({ filters: params.filters, page: params.page, limit: params.limit }),
    queryFn: (params, signal) => {
      const drill = parseDrillParams(params.filters, today);
      /* Unreachable while `enabled` follows the same parse; refused rather than sent malformed. */
      if (drill === null) return Promise.reject(new Error('Not a duty analytics drill'));
      return api.get<DutyAnalyticsRowsPageDto>(DUTY_ANALYTICS_ROWS_PATH, {
        query: drillQuery(drill, params.page, params.limit),
        signal,
      });
    },
    rowKey: (row) => row.key,
    enabled: target !== null,
  });

  if (target === null) return <NotFoundPage />;

  const data = table.query.data;
  const error = table.query.error;
  const refusal = error instanceof ApiError && error.status >= 400 && error.status < 500 ? error.message : null;

  const subject =
    data === undefined
      ? null
      : (data.cell.orgUnit?.name ??
        (data.cell.divisionId === null && data.cell.orgUnitId === null
          ? data.layout === 'COMMISSIONERATE'
            ? copy.allDivisions
            : copy.ownTotal
          : copy.unknownUnit));

  const columns: ColumnsType<DashboardTileRowDto> = visibleDrillColumns(data?.columns ?? []).map((column) => ({
    key: column.key,
    title: column.label,
    align: column.align,
    /* `DashboardTile`'s rendering verbatim: a worded vocabulary, and a cell with an `href` is a real link. */
    render: (_: unknown, row: DashboardTileRowDto) => {
      const value = vocabularyWord(column.vocabulary, row.cells[column.key] ?? '');
      const href = row.hrefs?.[column.key];
      return href === undefined || value === '' ? value : <NameCell name={value} to={href} />;
    },
  }));

  return (
    <div className="flex flex-col gap-6" data-testid="duty-analytics-rows-root" data-grain={data?.grain}>
      <PageHeader
        icon={ChartColumn}
        parent={{ label: copy.rowsParent, to: wayBackOf(target, raw.division) }}
        title={subject === null ? copy.columns[target.metric] : `${copy.columns[target.metric]} — ${subject}`}
        subtitle={data === undefined ? undefined : `${windowWords(data.window)} · ${copy.rowsInAll(data.figure)}`}
      />
      {refusal !== null ? (
        <div data-testid="duty-analytics-rows-refused">
          <EmptyState title={refusal} />
        </div>
      ) : error !== null && data === undefined ? (
        <EmptyState
          title={copy.rowsErrorTitle}
          description={copy.errorDescription}
          action={<Button onClick={() => void table.query.refetch()}>{copy.retry}</Button>}
        />
      ) : (
        <Table<DashboardTileRowDto>
          {...table.tableProps}
          columns={columns}
          size="middle"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <EmptyState size="sm" title={copy.rowsEmpty} /> }}
        />
      )}
    </div>
  );
};

export default DutyAnalyticsRowsPage;
